import { XCircleIcon } from '@heroicons/react/24/outline';
import {
  Component,
  ErrorInfo,
  lazy,
  ReactNode,
  startTransition,
  Suspense,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { TabOption, Tabs } from '@/components/chariot-ui';
import { FeatureLock } from '@/components/legacy-ui/FeatureLock';
import { createContactSupportButton } from '@/components/legacy-ui/utils';
import { Spinner } from '@/components/ui/spinner';
import { useFeatureLock } from '@/hooks/useFeatureLock';
import { defenseLevelColors } from '@/sections/attacks/constants';
import { SituationalAwarenessBanner } from '@/sections/validations/components/SituationalAwarenessBanner';
import { useAuth } from '@/state/auth';

// Re-export colors for external use
// eslint-disable-next-line react-refresh/only-export-components -- Re-exporting constants for convenience
export { defenseLevelColors };

// Lazy load tab components for code splitting
const DecoysTab = lazy(() => import('./tabs/DecoysTab'));
const GapAnalysisTab = lazy(() => import('./tabs/GapAnalysisTab'));
const OverviewTab = lazy(() => import('./tabs/OverviewTab'));
const SessionsTab = lazy(() => import('./tabs/SessionsTab'));

/**
 * Tab configuration for the Validations section.
 */
const allValidationTabs: TabOption[] = [
  { label: 'Overview', value: 'overview' },
  { label: 'Gap Analysis', value: 'gaps' },
  { label: 'Decoys', value: 'decoys' },
  { label: 'Sessions', value: 'sessions' },
];

const DEFAULT_TAB = 'overview';

// Tabs that opt out of the generic situational-awareness banner rendered below.
// 'gaps' renders its OWN data-aware banner (only when
// !isDemoOrg && !hasLiveData); 'overview' renders none — its widgets self-describe
// the not-assessed state via hasValidationData; 'decoys' hosts the Knossos
// deception platform, which is not situational-awareness related.
const BANNER_SELF_MANAGED_TABS = new Set(['overview', 'gaps', 'decoys']);

/**
 * ErrorBoundary component for catching and displaying React errors.
 * Used to wrap Suspense boundaries to provide graceful error handling.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Validations tab error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * Loading skeleton shown while lazy-loaded tabs are being fetched.
 */
function TabLoadingSkeleton() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <div className="text-chariot-text-secondary mb-2">Loading...</div>
      </div>
    </div>
  );
}

/**
 * Error fallback component shown when a tab fails to load.
 */
function TabErrorFallback() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4">
      <XCircleIcon className="text-chariot-status-critical size-12" />
      <div className="flex flex-col gap-2 text-center">
        <h2 className="text-chariot-text-primary text-lg font-bold">
          Failed to Load
        </h2>
        <p className="text-chariot-text-secondary text-sm">
          There was an error loading this tab. Please refresh the page or try
          again later.
        </p>
      </div>
    </div>
  );
}

/**
 * Validations Section (shown to users as "Detections")
 *
 * Provides a unified view of security posture through tabs:
 * - Overview: Executive dashboard with security posture score (Phase 3)
 * - Gap Analysis: ATT&CK vs D3FEND coverage gaps (Phase 4)
 * - Decoys: Knossos deception environments (gated by the `decoys` featureLock)
 * - Sessions: Live Breach & Attack Validation sessions (Praetorian-only)
 *
 * The four benchmark views (MITRE ATT&CK/D3FEND/EMB3D, NIST CSF) moved to
 * Insights → Benchmarks; old `?tab=<framework>` deep links redirect there.
 *
 * Features:
 * - URL-based tab state for shareable links and browser history
 * - Lazy loading with code splitting for each tab
 * - Suspense boundaries for loading states
 * - ErrorBoundary for graceful failure handling
 */
export function Validations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  const validationTabs = allValidationTabs;

  const validTabValues = useMemo(
    () => validationTabs.map(tab => tab.value),
    [validationTabs]
  );

  // Call useFeatureLock for the two gated tabs (Sessions is Praetorian-only;
  // Decoys requires the enableKnossos flag). The other six tabs are ungated
  // and render directly.
  const sessionsLock = useFeatureLock('validations.sessions');
  const decoysLock = useFeatureLock('decoys');
  const { isDemoOrg } = useAuth();

  // Map tab values to their lock results. Only 'sessions' and 'decoys' are
  // locked; every other tab falls through getTabLock's `{ locked: false }`
  // default.
  const tabLocks: Record<
    string,
    {
      locked: boolean;
      lockTitle?: string;
      lockDescription?: ReactNode;
      featureName?: string;
    }
  > = {
    sessions: sessionsLock,
    decoys: decoysLock,
  };

  // Get lock state for a tab
  const getTabLock = (tab: string) => {
    return tabLocks[tab] || { locked: false };
  };

  // Determine initial tab
  const getInitialTab = () => {
    if (validTabValues.includes(tabFromUrl || '')) {
      return tabFromUrl;
    }
    return DEFAULT_TAB;
  };

  const initialTab = getInitialTab();

  // Derive tab directly from URL (single source of truth, no state sync needed)
  const selectedTabValue = useMemo(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && validTabValues.includes(urlTab)) {
      return urlTab;
    }
    return initialTab || DEFAULT_TAB;
  }, [searchParams, validTabValues, initialTab]);

  // Custom setter that updates URL (which automatically updates selectedTabValue via useMemo)
  const setSelectedTab = (newTab: string) => {
    if (validTabValues.includes(newTab)) {
      // Use startTransition for non-urgent tab content loading
      // Keeps tab button responsive while content loads in background
      startTransition(() => {
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('tab', newTab);
            return newParams;
          },
          { replace: true }
        );
      });
    }
  };

  // Find the selected tab object for the Tabs component
  const selectedTab =
    validationTabs.find(tab => tab.value === selectedTabValue) ||
    validationTabs[0];

  // Handle tab selection
  const handleTabSelect = (tab: TabOption) => {
    setSelectedTab(tab.value as string);
  };

  const renderContent = () => {
    // Only the sessions and decoys tabs are feature-locked, so only block on
    // the selected tab's own lock load state. The ungated tabs must not wait
    // on either entitlement lookup.
    if (
      (selectedTabValue === 'sessions' && sessionsLock.isLoading) ||
      (selectedTabValue === 'decoys' && decoysLock.isLoading)
    ) {
      return <Spinner centered size="lg" className="h-full" />;
    }

    const { locked, lockTitle, lockDescription, featureName } =
      getTabLock(selectedTabValue);

    const content = (() => {
      switch (selectedTabValue) {
        case 'gaps':
          return <GapAnalysisTab />;
        case 'decoys':
          return <DecoysTab />;
        case 'sessions':
          return <SessionsTab />;
        case 'overview':
        default:
          return <OverviewTab />;
      }
    })();

    return (
      <ErrorBoundary fallback={<TabErrorFallback />}>
        <Suspense fallback={<TabLoadingSkeleton />}>
          <FeatureLock
            locked={locked}
            title={lockTitle}
            description={lockDescription}
            action={createContactSupportButton(featureName)}
          >
            {!isDemoOrg && !BANNER_SELF_MANAGED_TABS.has(selectedTabValue) && (
              <SituationalAwarenessBanner />
            )}
            {content}
          </FeatureLock>
        </Suspense>
      </ErrorBoundary>
    );
  };

  return (
    <AppRouteBody title="Detections">
      <div className="flex h-full flex-col overflow-hidden">
        <Tabs
          tabs={validationTabs}
          selected={selectedTab}
          onSelect={handleTabSelect}
          variant="primary"
        />
        <div className="mt-4 h-full overflow-y-auto pb-6">
          {renderContent()}
        </div>
      </div>
    </AppRouteBody>
  );
}
